// What a SUPPLIER-BOUND email says, when a TRADER's action triggers it.
//
// ─── The bug this exists to stop ─────────────────────────────────────────
// A trader accepted an offer on M-10087 and the supplier received an English
// `offer_accepted` email whose subject line read «كفرات iPhone 15». The title
// had been picked with `title_ar || title_en || title_zh` — Arabic first,
// unconditionally, because the code that built it lives on the trader's side
// of the product and every other string around it is for the trader.
//
// That pattern is correct for anything the TRADER reads and wrong for
// everything the SUPPLIER reads, and the two sat inches apart in the same
// function: one `productName` feeding a trilingual in-app notification (fine)
// and the same variable feeding an email to China (not fine).
//
// ─── The rule (owner, 2026-09-22) ────────────────────────────────────────
// Supplier-bound email content is ENGLISH FIRST: title_en → title_zh →
// title_ar, taking the first non-blank. English before Chinese because it is
// the language the whole supplier-facing mail estate already speaks —
// offer_lost is English-only by the same owner's decision on the same day —
// and Arabic LAST because for a Chinese factory it is the one option that
// carries no information at all.
//
// ⚠️ THE SAME RULE EXISTS IN SQL, AND THE TWO MUST MOVE TOGETHER.
// notify_request_closed() (migration 20260922144335) builds the offer_lost
// subject line with its own coalesce. It shipped as en → **ar** → zh; the
// owner aligned it to en → zh → ar on 2026-09-22, in
// supabase/migrations/PENDING_offer_lost_title_order.sql.
//
// Both orders are English-first, so they agree on every request that HAS a
// title_en — which is why the live M-10087 test could not have caught the
// difference. They part only on a request with no English title.
//
// This is the isReviewable()/fetchReadyCount() hazard CLAUDE.md records,
// wearing a different hat: one rule, two expressions, two languages. Change
// this file and you owe a migration; change that trigger and you owe this
// file.
//
// ─── One predicate, one place ────────────────────────────────────────────
// Golden rule 12: the reason this is a module and not four inline
// expressions is that a fallback chain restated per call site is a fallback
// chain that drifts per call site. That is exactly how the trader-first order
// ended up on an email in the first place.

const firstNonBlank = (...values) => {
  for (const v of values) {
    if (typeof v === 'string' && v.trim() !== '') return v.trim();
  }
  return '';
};

// A request's title, as a SUPPLIER should read it.
// Pass the row; missing columns are simply skipped, so a caller that forgot to
// select title_zh degrades to en → ar instead of throwing.
export function supplierEmailTitle(row, fallback = '') {
  return firstNonBlank(row?.title_en, row?.title_zh, row?.title_ar) || fallback;
}

// A product's name, as a SUPPLIER should read it. Same order, different
// columns — products carry name_*, requests carry title_*.
export function supplierEmailProductName(row, fallback = '') {
  return firstNonBlank(row?.name_en, row?.name_zh, row?.name_ar) || fallback;
}

// ⚠️ THERE IS DELIBERATELY NO supplierEmailLang() HERE.
//
// The other half of the same bug was `lang` being passed to supplier-bound
// templates holding the TRADER's UI language, so a Saudi trader paying a
// Chinese factory sent that factory an Arabic email. The fix is to pass
// NOTHING: every supplier-bound template in send-email/index.ts reads
// `d.lang || 'en'`, so an omitted lang is already English, and English is what
// the supplier-facing estate speaks.
//
// A helper here would invite `lang: supplierEmailLang(...)` and a guess. The
// right input is the supplier's OWN profiles.lang — which exists and is
// populated for 47 of 47 suppliers (ar/en/zh) and is what the admin console
// already uses (consoleVerification.js, AdminSupplierDetail.jsx). Using it
// from these trader-side pages needs a profile fetch each call site, so it is
// a follow-up, not this fix. Until then: omit lang, get English.
